/**
 * WaitingQueueRow — one row of the pinned pane's "waiting on a person" queue.
 *
 * Shared by ora and the Outlook add-in (`@product-app/common/workflows/…`);
 * there is no forked copy under the add-in tree (design Constraint 20).
 *
 * The row is a single focusable control inside a semantic list item, so the
 * whole queue is operable by keyboard with no custom key handling. Every
 * indicator it carries — item type, group membership (via its header), the
 * elapsed wait — is TEXT. Nothing is signalled by colour or position alone
 * (Accessibility DoD).
 *
 * It shows an ELAPSED wait only. There is no remaining-time, ETA or
 * progress-estimate element anywhere in this component, and `formatElapsedWait`
 * cannot produce one.
 */

import React from "react";
import { formatElapsedWait } from "../formatTime";
import { renderInlineBold, stripInlineBold } from "../renderInlineBold";
import type { WaitingQueueItem } from "../types";

/** Human label for the row's `item_type`. "run" is the only member today —
 *  the field is a declared extension point so a prepared brief can join the
 *  queue later without reshaping the row. */
export function itemTypeLabel(itemType: string | null | undefined): string {
  switch ((itemType ?? "").toLowerCase()) {
    case "run":
      return "Run";
    case "brief":
      return "Brief";
    default:
      return itemType && itemType.trim().length > 0 ? itemType : "Item";
  }
}

/** "Acme Holdings · POL-123", with either half omitted when absent. */
export function subjectLine(item: WaitingQueueItem): string {
  const parts = [item.client_name, item.policy_number].filter(
    (p): p is string => typeof p === "string" && p.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(" · ") : "Client not identified";
}

function elapsedTone(
  sinceIso: string | null | undefined,
  now: Date,
): { color: string; bg: string; border: string } {
  if (!sinceIso) {
    return {
      color: "text-primary-gray-light-500",
      bg: "bg-primary-gray-light-50",
      border: "border-primary-gray-light-200",
    };
  }
  const ms = now.getTime() - new Date(sinceIso).getTime();
  if (Number.isNaN(ms) || ms < 0) {
    return {
      color: "text-primary-gray-light-500",
      bg: "bg-primary-gray-light-50",
      border: "border-primary-gray-light-200",
    };
  }
  if (ms >= 45 * 60 * 1000) {
    return {
      color: "text-primary-error-700",
      bg: "bg-primary-error-50",
      border: "border-primary-error-200",
    };
  }
  if (ms >= 15 * 60 * 1000) {
    return {
      color: "text-primary-warning-700",
      bg: "bg-primary-warning-50",
      border: "border-primary-warning-200",
    };
  }
  return {
    color: "text-primary-gray-light-500",
    bg: "bg-primary-gray-light-50",
    border: "border-primary-gray-light-200",
  };
}

export interface WaitingQueueRowProps {
  item: WaitingQueueItem;
  /** Raised to the host — the row never navigates by itself. */
  onOpenItem: (instanceId: string) => void;
  /** Injected only so tests and the render probe can pin "now". */
  now?: Date;
  /** First needs-you tile: yellow frame + Do this next bar. */
  featured?: boolean;
}

export function WaitingQueueRow({
  item,
  onOpenItem,
  now,
  featured = false,
}: WaitingQueueRowProps): React.ReactElement {
  const clock = now ?? new Date();
  const elapsed = formatElapsedWait(item.waiting_since, clock);
  const type = itemTypeLabel(item.item_type);
  const client =
    typeof item.client_name === "string" && item.client_name.trim()
      ? item.client_name.trim()
      : "Client not identified";
  const policy =
    typeof item.policy_number === "string" && item.policy_number.trim()
      ? item.policy_number.trim()
      : null;
  const waitingFor = item.waiting_for?.trim() || "Waiting for a response";
  const tone = elapsedTone(item.waiting_since, clock);

  // The accessible name carries the SPELLED-OUT wait ("waiting 45 minutes"),
  // not just the "45m" badge — the badge alone is a visual shorthand.
  // `waiting_for` is the HITL prompt, which may carry `**bold**`: the label
  // gets the marker-free text so a screen reader never reads the asterisks.
  const accessibleName = `${type}. ${subjectLine(item)}. ${stripInlineBold(waitingFor)}. ${elapsed.aria}.${
    featured ? " Action needed." : ""
  }`;

  return (
    <li className="list-none">
      <button
        type="button"
        onClick={() => onOpenItem(item.instance_id)}
        aria-label={accessibleName}
        data-testid="waiting-queue-row"
        data-instance-id={item.instance_id}
        data-needs-you={item.needs_you ? "true" : "false"}
        data-featured={featured ? "true" : "false"}
        className={
          "flex w-full flex-col text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-sunrise " +
          (featured
            ? "rounded-lg border border-sunrise bg-[#FFFDF0] p-[15px]"
            : "rounded-lg border border-primary-gray-light-200 bg-base-white p-[15px] hover:border-primary-gray-light-300")
        }
      >
        <span className="flex items-start gap-2.5">
          <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
            <span className="flex flex-wrap items-baseline gap-x-[7px]">
              <span className="text-[15px] font-semibold leading-[21px] text-primary-gray-light-900">
                {client}
              </span>
              {policy ? (
                <span className="text-xs leading-[21px] text-primary-gray-light-500">
                  · {policy}
                </span>
              ) : null}
            </span>
            <span className="break-words text-[13px] leading-[19px] text-primary-gray-light-600">
              {renderInlineBold(waitingFor)}
            </span>
          </span>
          <span
            aria-hidden="true"
            data-testid="waiting-queue-row-elapsed"
            className={
              "inline-flex shrink-0 items-center gap-1 rounded-[10px] border px-[9px] py-[3px] text-[11px] font-semibold " +
              `${tone.color} ${tone.bg} ${tone.border}`
            }
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className="flex-shrink-0"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            {elapsed.label}
          </span>
        </span>
        {featured ? (
          <span
            data-testid="waiting-queue-row-action"
            className="mt-2 flex items-center justify-center gap-2 rounded-md bg-sunrise px-[11px] py-[11px] text-[13px] font-semibold text-primary-gray-light-900"
          >
            Do this next →
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </span>
        ) : null}
      </button>
    </li>
  );
}

export default WaitingQueueRow;
